import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '../../lib/utils';
import {
  Search,
  Moon,
  Sun,
  Command,
  Shuffle,
  ChevronRight,
  Home,
  Menu,
  X,
  ArrowLeft
} from 'lucide-react';

type View = 'mandalas' | 'hymns' | 'verse';

interface BreadcrumbItem {
  label: string;
  shortLabel?: string;
  onClick?: () => void;
}

interface DynamicIslandProps {
  currentView: View;
  selectedMandala: number | null;
  currentHymnData: {
    mandala: number;
    hymn_number: number;
    title?: string;
    deity?: string;
  } | null;
  activeVerseNumber?: number;
  darkMode: boolean;
  isLoading?: boolean;
  onToggleDarkMode: () => void;
  onOpenSearch: () => void;
  onRandomVerse: () => void;
  onNavigateHome: () => void;
  onNavigateToMandala?: (mandalaNumber: number) => void;
  onNavigateBack: () => void;
  onToggleSidebar: () => void;
  sidebarOpen: boolean;
}

// Snappy spring animation config
const springConfig = {
  type: "spring" as const,
  stiffness: 500,
  damping: 35,
  mass: 0.8
};

export const DynamicIsland: React.FC<DynamicIslandProps> = ({
  currentView,
  selectedMandala,
  currentHymnData,
  activeVerseNumber,
  darkMode,
  isLoading,
  onToggleDarkMode,
  onOpenSearch,
  onRandomVerse,
  onNavigateHome,
  onNavigateBack,
  onToggleSidebar,
  sidebarOpen
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  // Track scroll to auto-collapse
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50 && isExpanded) {
        setIsExpanded(false);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isExpanded]);

  // Build breadcrumb based on current view
  const getBreadcrumbs = (): BreadcrumbItem[] => {
    const crumbs: BreadcrumbItem[] = [
      { label: 'Rig Veda', shortLabel: 'RV', onClick: onNavigateHome }
    ];

    if (selectedMandala && (currentView === 'hymns' || currentView === 'verse')) {
      crumbs.push({
        label: `Mandala ${selectedMandala}`,
        shortLabel: `M${selectedMandala}`,
        onClick: currentView === 'verse' ? onNavigateBack : undefined
      });
    }

    if (currentHymnData && currentView === 'verse') {
      crumbs.push({
        label: `Hymn ${currentHymnData.hymn_number}`,
        shortLabel: `H${currentHymnData.hymn_number}`
      });

      if (activeVerseNumber) {
        crumbs.push({
          label: `Verse ${activeVerseNumber}`,
          shortLabel: `V${activeVerseNumber}`
        });
      }
    }

    return crumbs;
  };

  const breadcrumbs = getBreadcrumbs();

  // Get contextual display text for collapsed state
  const getContextText = (): string => {
    if (currentView === 'verse' && currentHymnData) {
      return `${currentHymnData.mandala}.${currentHymnData.hymn_number}${activeVerseNumber ? `.${activeVerseNumber}` : ''}`;
    }
    if (currentView === 'hymns' && selectedMandala) {
      return `Mandala ${selectedMandala}`;
    }
    return 'Explore';
  };

  return (
    <header className="sticky top-0 z-40">
      {/* Desktop Dynamic Island */}
      <div className="hidden md:block">
        <div className="flex justify-center pt-4">
          <motion.div
            layout
            transition={springConfig}
            onHoverStart={() => setIsExpanded(true)}
            onHoverEnd={() => setIsExpanded(false)}
            className={cn(
              "relative",
              "bg-museum-900/95 dark:bg-museum-100/95",
              "backdrop-blur-xl",
              "shadow-2xl shadow-black/20 dark:shadow-black/40",
              "cursor-pointer",
              "overflow-hidden"
            )}
            style={{
              borderRadius: isExpanded ? 16 : 28,
            }}
          >
            <AnimatePresence mode="wait">
              {!isExpanded ? (
                // Collapsed State - Pill
                <motion.div
                  key="collapsed"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.08 }}
                  className="flex items-center gap-3 px-4 py-2.5"
                >
                  {/* Context Badge */}
                  <div className={cn(
                    "flex items-center gap-2",
                    "px-2 py-1 rounded-full",
                    "bg-museum-800 dark:bg-museum-200"
                  )}>
                    <span className="font-mono text-[10px] font-medium uppercase tracking-wider text-museum-400 dark:text-museum-600">
                      {selectedMandala ? selectedMandala.toString().padStart(2, '0') : '●'}
                    </span>
                  </div>

                  {/* Location Text */}
                  <span className="font-display text-sm text-museum-100 dark:text-museum-800 min-w-[80px]">
                    {getContextText()}
                  </span>

                  {/* Divider */}
                  <div className="h-4 w-px bg-museum-700 dark:bg-museum-300" />

                  {/* Search Hint */}
                  <div className="flex items-center gap-1 text-museum-400 dark:text-museum-500">
                    <Command className="h-3 w-3" />
                    <span className="font-mono text-[10px]">K</span>
                  </div>

                  {/* Theme Toggle */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onToggleDarkMode();
                    }}
                    className="p-1 text-museum-400 dark:text-museum-500 hover:text-museum-200 dark:hover:text-museum-700 transition-colors"
                  >
                    {darkMode ? (
                      <Sun className="h-3.5 w-3.5" />
                    ) : (
                      <Moon className="h-3.5 w-3.5" />
                    )}
                  </button>
                </motion.div>
              ) : (
                // Expanded State
                <motion.div
                  key="expanded"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.08 }}
                  className="w-[400px] p-4"
                >
                  {/* Header Row */}
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h1 className="font-display text-base text-museum-100 dark:text-museum-800">
                        Veda Explorer
                      </h1>
                      <p className="font-mono text-[9px] uppercase tracking-[0.15em] text-museum-500 dark:text-museum-400">
                        Sacred Archive
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onToggleDarkMode();
                        }}
                        className="p-1.5 text-museum-400 dark:text-museum-500 hover:text-museum-200 dark:hover:text-museum-700 transition-colors"
                      >
                        {darkMode ? (
                          <Sun className="h-4 w-4" />
                        ) : (
                          <Moon className="h-4 w-4" />
                        )}
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onOpenSearch();
                        }}
                        className="p-1.5 text-museum-400 dark:text-museum-500 hover:text-museum-200 dark:hover:text-museum-700 transition-colors"
                      >
                        <Search className="h-4 w-4" />
                      </button>
                    </div>
                  </div>

                  {/* Divider */}
                  <div className="h-px bg-museum-700 dark:bg-museum-300 mb-3" />

                  {/* Breadcrumb */}
                  <nav className="flex items-center gap-1 flex-wrap mb-3">
                    {breadcrumbs.map((crumb, index) => (
                      <React.Fragment key={index}>
                        {index > 0 && (
                          <ChevronRight className="h-3 w-3 text-museum-600 dark:text-museum-400 flex-shrink-0" />
                        )}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            crumb.onClick?.();
                          }}
                          disabled={!crumb.onClick}
                          className={cn(
                            "font-mono text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded transition-colors",
                            crumb.onClick
                              ? "text-museum-300 dark:text-museum-600 hover:text-museum-100 dark:hover:text-museum-800 hover:bg-museum-800 dark:hover:bg-museum-200"
                              : "text-museum-100 dark:text-museum-800"
                          )}
                        >
                          {crumb.label}
                        </button>
                      </React.Fragment>
                    ))}
                  </nav>

                  {/* Search Bar */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onOpenSearch();
                    }}
                    className={cn(
                      "w-full flex items-center gap-2 px-3 py-2",
                      "bg-museum-800 dark:bg-museum-200",
                      "rounded-lg",
                      "text-museum-400 dark:text-museum-500",
                      "hover:bg-museum-700 dark:hover:bg-museum-300",
                      "transition-colors"
                    )}
                  >
                    <Search className="h-4 w-4" />
                    <span className="font-body text-sm">Search the archive...</span>
                    <kbd className="ml-auto flex items-center gap-0.5 px-1.5 py-0.5 text-[9px] font-mono bg-museum-700 dark:bg-museum-300 rounded">
                      <Command className="h-2.5 w-2.5" />K
                    </kbd>
                  </button>

                  {/* Quick Actions */}
                  <div className="flex items-center justify-between mt-3 pt-3 border-t border-museum-700 dark:border-museum-300">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onNavigateHome();
                      }}
                      className={cn(
                        "flex items-center gap-2 px-2 py-1.5",
                        "font-mono text-[10px] uppercase tracking-wider",
                        "text-museum-400 dark:text-museum-500",
                        "hover:text-museum-200 dark:hover:text-museum-700",
                        "transition-colors"
                      )}
                    >
                      <Home className="h-3.5 w-3.5" />
                      Home
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onRandomVerse();
                      }}
                      disabled={isLoading}
                      className={cn(
                        "flex items-center gap-2 px-2 py-1.5",
                        "font-mono text-[10px] uppercase tracking-wider",
                        "text-museum-400 dark:text-museum-500",
                        "hover:text-museum-200 dark:hover:text-museum-700",
                        "transition-colors"
                      )}
                    >
                      <Shuffle className="h-3.5 w-3.5" />
                      Random
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </div>

        {/* Background blur area for click-away */}
        {isExpanded && (
          <div
            className="fixed inset-0 -z-10"
            onClick={() => setIsExpanded(false)}
          />
        )}
      </div>

      {/* Mobile Header - Simpler but still dynamic */}
      <div className="md:hidden">
        <motion.div
          layout
          transition={springConfig}
          className={cn(
            "bg-white/95 dark:bg-museum-950/95",
            "backdrop-blur-sm",
            "border-b border-museum-200 dark:border-museum-800"
          )}
        >
          <div className="px-4 py-3">
            <div className="flex items-center justify-between gap-4">
              {/* Left: Menu or Back */}
              {currentView === 'mandalas' ? (
                <button
                  className="p-2 -ml-2 text-museum-500 hover:text-museum-700 dark:text-museum-400 dark:hover:text-museum-200 transition-colors"
                  onClick={onToggleSidebar}
                  aria-label="Toggle navigation"
                >
                  {sidebarOpen ? (
                    <X className="h-5 w-5" strokeWidth={1.5} />
                  ) : (
                    <Menu className="h-5 w-5" strokeWidth={1.5} />
                  )}
                </button>
              ) : (
                <button
                  className="p-2 -ml-2 text-museum-500 hover:text-museum-700 dark:text-museum-400 dark:hover:text-museum-200 transition-colors"
                  onClick={onNavigateBack}
                  aria-label="Go back"
                >
                  <ArrowLeft className="h-5 w-5" strokeWidth={1.5} />
                </button>
              )}

              {/* Center: Context */}
              <div className="flex-1 text-center">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={currentView + String(selectedMandala)}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -6 }}
                    transition={{ duration: 0.12 }}
                  >
                    {currentView === 'mandalas' && (
                      <span className="font-display text-base text-museum-900 dark:text-museum-100">
                        Veda Explorer
                      </span>
                    )}
                    {currentView === 'hymns' && selectedMandala && (
                      <span className="font-display text-base text-museum-900 dark:text-museum-100">
                        Mandala {selectedMandala}
                      </span>
                    )}
                    {currentView === 'verse' && currentHymnData && (
                      <span className="font-display text-base text-museum-900 dark:text-museum-100">
                        {currentHymnData.mandala}.{currentHymnData.hymn_number}
                        {activeVerseNumber && `.${activeVerseNumber}`}
                      </span>
                    )}
                  </motion.div>
                </AnimatePresence>
              </div>

              {/* Right: Actions */}
              <div className="flex items-center gap-1">
                <button
                  className="p-2 text-museum-400 hover:text-museum-700 dark:text-museum-500 dark:hover:text-museum-200 transition-colors"
                  onClick={onOpenSearch}
                  aria-label="Search"
                >
                  <Search className="h-5 w-5" strokeWidth={1.5} />
                </button>
                <button
                  className="p-2 text-museum-400 hover:text-museum-700 dark:text-museum-500 dark:hover:text-museum-200 transition-colors"
                  onClick={onToggleDarkMode}
                  aria-label={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
                >
                  {darkMode ? (
                    <Sun className="h-5 w-5" strokeWidth={1.5} />
                  ) : (
                    <Moon className="h-5 w-5" strokeWidth={1.5} />
                  )}
                </button>
              </div>
            </div>

            {/* Mobile Breadcrumb - Simplified */}
            {currentView !== 'mandalas' && (
              <motion.nav
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="flex items-center gap-1 pt-2 mt-2 border-t border-museum-100 dark:border-museum-800 overflow-x-auto"
              >
                {breadcrumbs.map((crumb, index) => (
                  <React.Fragment key={index}>
                    {index > 0 && (
                      <ChevronRight className="h-3 w-3 text-museum-300 dark:text-museum-600 flex-shrink-0" />
                    )}
                    <button
                      onClick={crumb.onClick}
                      disabled={!crumb.onClick}
                      className={cn(
                        "font-mono text-[9px] uppercase tracking-wider whitespace-nowrap px-1.5 py-0.5 rounded transition-colors flex-shrink-0",
                        crumb.onClick
                          ? "text-museum-500 dark:text-museum-400 hover:text-museum-700 dark:hover:text-museum-200"
                          : "text-museum-700 dark:text-museum-300"
                      )}
                    >
                      {crumb.shortLabel || crumb.label}
                    </button>
                  </React.Fragment>
                ))}
              </motion.nav>
            )}
          </div>
        </motion.div>
      </div>
    </header>
  );
};

export default DynamicIsland;
